import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../lib/api";
import { useNavigate } from "react-router-dom";
import { Search, SunMedium, ChevronRight, ChevronDown, GitCommitHorizontal, Gauge, PoundSterling, Wrench, ShieldCheck, PenTool, FileText, Activity } from "lucide-react";
import { Pager } from "../Pager";

const spaced = (n) => Math.round(Number(n || 0)).toLocaleString("en-GB").replace(/,/g, " ");
const gbpK = (n) => {
  const v = Number(n || 0);
  return Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : spaced(v);
};
const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-").map(Number);
  return new Date(y, mo - 1, 1).toLocaleString("en-GB", { month: "short", year: "numeric" });
};

function Kpi({ label, value, unit, prefix, sub, onClick, testid }) {
  return (
    <button onClick={onClick} className="vl-kpi h-full w-full text-left" data-testid={testid}>
      <div className="vl-kpi-label">{label}</div>
      <div className="vl-kpi-value" data-testid={`${testid}-value`}>
        {prefix && <span className="vl-kpi-prefix">{prefix}</span>}
        {value}
        {unit && <span className="vl-kpi-unit">{unit}</span>}
      </div>
      <div className="vl-kpi-sub">{sub}</div>
    </button>
  );
}

const kpiWidget = (id, title, build) => ({
  id, title, w: 3, h: 2, minW: 2, minH: 2, bare: true,
  Render: ({ sources: s }) => {
    const nav = useNavigate();
    const k = s.overview?.kpis;
    return <Kpi {...build(k, nav)} testid={`kpi-${id}`} />;
  },
});

export const STATUS_STYLE = {
  active: "vl-pill-green", no_data: "vl-pill-amber", installing: "vl-pill-amber",
  pipeline: "vl-pill-grey", retired: "vl-pill-grey",
};

function StatusPill({ status }) {
  return (
    <span className={`vl-pill ${STATUS_STYLE[status.key] || "vl-pill-grey"}`} data-testid="site-status-pill">
      <span className="vl-dot" />{status.label}
    </span>
  );
}

function Lifecycle({ pct, years }) {
  if (pct == null) return <span className="vl-muted">—</span>;
  return (
    <div className="flex items-center gap-2" title={`${years} of 25 years`}>
      <div className="vl-bar"><div className="vl-bar-fill" style={{ width: `${Math.max(3, pct)}%` }} /></div>
      <span className="vl-mono vl-muted text-[11px]">{years}y</span>
    </div>
  );
}

const ago = (iso) => {
  if (!iso) return "—";
  const m = Math.max(0, (Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${Math.round(m)}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  if (m < 43200) return `${Math.round(m / 1440)}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const FILTERS = [["all", "All"], ["operational", "Operational"], ["installing", "Installing"], ["pipeline", "Pipeline"]];

const KIND_ICON = { stage: GitCommitHorizontal, meter: Gauge, income: PoundSterling, opex: Wrench, asset: ShieldCheck, design: PenTool, document: FileText, other: Activity };

function ActivityLine({ a, compact }) {
  const Icon = KIND_ICON[a.kind] || Activity;
  return (
    <span className={`vl-act vl-act-${a.kind} ${compact ? "is-compact" : ""}`} data-testid={compact ? "site-last-activity" : `activity-item-${a.id}`}>
      <Icon size={compact ? 11 : 13} />
      <span className="truncate">{a.label}</span>
      {!compact && <span className="vl-muted vl-mono text-[10px] ml-auto shrink-0">{a.actor} · {ago(a.at)}</span>}
    </span>
  );
}

function ActivityRow({ id, cols }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/dashboard/site-activity/${id}?limit=8`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [id]);
  return (
    <tr className="vl-act-row" data-testid={`site-activity-${id}`}>
      <td colSpan={cols}>
        {!items ? <span className="vl-muted text-xs">Loading activity…</span>
          : items.length === 0 ? <span className="vl-muted text-xs" data-testid="site-activity-empty">No recorded activity yet.</span>
          : <div className="vl-timeline">{items.map((a) => <ActivityLine key={a.id} a={a} />)}</div>}
      </td>
    </tr>
  );
}

function SiteRow({ r, onOpen, open, onToggle }) {
  return (
    <>
    <tr onClick={onOpen} className={`vl-row ${open ? "is-open" : ""}`} data-testid={`site-row-${r.id}`}>
      <td>
        <div className="flex items-center gap-3 min-w-0">
          <span className="vl-tile"><SunMedium size={14} /></span>
          <div className="min-w-0">
            <div className="vl-row-title truncate">{r.name}</div>
            <div className="vl-mono vl-muted text-[11px] truncate">{r.code || "—"}{r.kwp ? ` · ${spaced(r.kwp)} kWp` : ""}</div>
            {r.last_activity && <ActivityLine a={r.last_activity} compact />}
          </div>
        </div>
      </td>
      <td>
        {r.manager ? (
          <span className="inline-flex items-center gap-2"><span className="vl-avatar">{r.manager_initials}</span><span className="truncate">{r.manager}</span></span>
        ) : <span className="vl-muted">Unassigned</span>}
      </td>
      <td><span className={`vl-chip vl-chip-${r.ownership.toLowerCase().replace("-fit", "").replace(" ", "_")}`}>{r.ownership}</span></td>
      <td className="vl-mono">{r.monthly_income_gbp != null ? `GBP ${spaced(r.monthly_income_gbp)}` : <span className="vl-muted">—</span>}</td>
      <td><Lifecycle pct={r.lifecycle_pct} years={r.lifecycle_years} /></td>
      <td onClick={(e) => e.stopPropagation()}>
        <button onClick={onToggle} className="vl-act-btn" title={open ? "Hide activity" : "Show recent activity"} data-testid={`site-activity-toggle-${r.id}`}>
          <span className="vl-mono text-[11px]" title={r.updated_at || ""} data-testid={`site-updated-${r.id}`}>{ago(r.updated_at)}</span>
          <ChevronDown size={13} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
        </button>
      </td>
      <td>
        <div className="flex items-center justify-between gap-2">
          <StatusPill status={r.status} />
          <ChevronRight size={14} className="vl-chev" />
        </div>
      </td>
    </tr>
    {open && <ActivityRow id={r.id} cols={7} />}
    </>
  );
}

function SitesRegister({ sources: s, fitHeight }) {
  const nav = useNavigate();
  const rootRef = useRef(null);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || !fitHeight) return undefined;
    const ro = new ResizeObserver(() => fitHeight(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [fitHeight]);
  const [q, setQ] = useState("");
  const [f, setF] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(() => { const v = +localStorage.getItem("eneprox.dash.sitesPerPage"); return [5, 10, 25, 50].includes(v) ? v : 5; });
  const rows = s.overview?.rows;
  const shown = useMemo(() => (rows || []).filter((r) => {
    if (f !== "all" && r.group !== f) return false;
    const t = q.trim().toLowerCase();
    return !t || [r.name, r.code, r.postcode, r.manager, r.ownership].some((x) => (x || "").toLowerCase().includes(t));
  }), [rows, q, f]);
  const pages = Math.max(1, Math.ceil(shown.length / per));
  const cur = Math.min(page, pages);
  const pageRows = shown.slice((cur - 1) * per, cur * per);
  const counts = useMemo(() => {
    const c = { all: (rows || []).length };
    (rows || []).forEach((r) => { c[r.group] = (c[r.group] || 0) + 1; });
    return c;
  }, [rows]);

  return (
    <div ref={rootRef} className="flex flex-col" data-testid="w-sites-register">
      <div className="flex items-center justify-between gap-3 flex-wrap px-4 pt-4 pb-3" onMouseDown={(e) => e.stopPropagation()}>
        <label className="vl-search">
          <Search size={14} />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search sites, postcodes, managers"
            data-testid="sites-register-search" />
        </label>
        <div className="vl-seg" role="tablist">
          {FILTERS.map(([k, label]) => (
            <button key={k} onClick={() => { setF(k); setPage(1); }} className={f === k ? "is-on" : ""} data-testid={`sites-filter-${k}`}>
              {label}{counts[k] ? <span className="vl-seg-n">{counts[k]}</span> : null}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="vl-table">
          <thead>
            <tr><th>Site</th><th>Asset manager</th><th>Book</th><th>Monthly income</th><th>Lifecycle</th><th>Updated</th><th>Status</th></tr>
          </thead>
          <tbody>
            {pageRows.map((r) => <SiteRow key={r.id} r={r} onOpen={() => nav(`/projects/${r.id}`)} open={openId === r.id} onToggle={() => setOpenId(openId === r.id ? null : r.id)} />)}
          </tbody>
        </table>
        {rows && shown.length === 0 && <div className="vl-muted text-sm text-center py-10" data-testid="sites-register-empty">No sites match.</div>}
        {!rows && <div className="vl-muted text-sm text-center py-10">Loading…</div>}
      </div>
      {shown.length > 0 && (
        <div onMouseDown={(e) => e.stopPropagation()}>
          <Pager total={shown.length} page={cur} perPage={per} sizes={[5, 10, 25, 50]} testid="sites-register-pager"
            onPage={(n) => setPage(Math.min(Math.max(1, n), pages))}
            onPerPage={(n) => { setPer(n); setPage(1); localStorage.setItem("eneprox.dash.sitesPerPage", String(n)); }} />
        </div>
      )}
    </div>
  );
}

export const VELORY_WIDGETS = [
  kpiWidget("kpi_sites", "Total sites", (k, nav) => ({
    label: "Total sites", value: k ? spaced(k.total_sites) : "—",
    sub: k ? `${spaced(k.total_kwp)} kWp · ${k.operational} operational` : "Owned and managed",
    onClick: () => nav("/projects"),
  })),
  kpiWidget("kpi_income", "Monthly income", (k, nav) => ({
    label: "Monthly income", prefix: "GBP", value: k ? gbpK(k.monthly_income_gbp) : "—",
    sub: k?.income_month ? `Latest month per site · to ${monthLabel(k.income_month)}` : "No income recorded",
    onClick: () => nav("/income"),
  })),
  kpiWidget("kpi_generating", "Generating", (k, nav) => ({
    label: "Generating", value: k?.generating_pct != null ? k.generating_pct : "—", unit: k?.generating_pct != null ? "%" : "",
    sub: k ? `${k.generating} live · ${k.offline} without data (3d)` : "Operational sites",
    onClick: () => nav("/analytics"),
  })),
  kpiWidget("kpi_renewals", "Due for renewal", (k, nav) => ({
    label: "Due for renewal", value: k ? spaced(k.renewals_due) : "—",
    sub: `Warranties in the next ${k?.renewal_window_days || 90} days`,
    onClick: () => nav("/assets?view=board"),
  })),
  { id: "sites_register", title: "Recently updated sites", w: 12, h: 7, minW: 6, minH: 4, bare: true, Render: SitesRegister },
];

export const VELORY_LAYOUT = [
  { i: "kpi_sites", x: 0, y: 0, w: 3, h: 2 },
  { i: "kpi_income", x: 3, y: 0, w: 3, h: 2 },
  { i: "kpi_generating", x: 6, y: 0, w: 3, h: 2 },
  { i: "kpi_renewals", x: 9, y: 0, w: 3, h: 2 },
  { i: "sites_register", x: 0, y: 2, w: 12, h: 7 },
];
export const VELORY_HEIGHT = 9;
export const LAYOUT_VERSION = "2";
